/**
 * 사업장 기본 확인자
 * ---------------------------------------------------------------------------
 * 일지를 새로 만들 때 여기 등록해 둔 확인자를 그대로 가져온다. 매일 같은
 * 사람을 다시 고르게 하면 아무도 고르지 않고, 결재란이 빈 일지가 쌓인다.
 *
 * 순서는 결재란 순서다. 일지에서 그 일지만 따로 바꿀 수 있다.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App as AntApp,
  Button,
  Empty,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { LogApprover, Member } from '@eco/shared';
import { api, errorMessage } from '../../lib/api';
import { HelpH, HelpP, PageHelp } from '../../components/PageHelp';

const { Text } = Typography;

interface Chosen {
  userId: string;
  name: string;
}

export function SiteApproverDialog({
  open,
  siteId,
  siteName,
  readOnly,
  onClose,
}: {
  open: boolean;
  siteId: string | null;
  siteName: string;
  readOnly: boolean;
  onClose: () => void;
}) {
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Chosen[]>([]);

  const members = useQuery<Member[]>({
    queryKey: ['members'],
    queryFn: async () => (await api.get<{ items: Member[] }>('/daily-logs/members')).data.items,
    enabled: open,
  });

  const current = useQuery<LogApprover[]>({
    queryKey: ['site-approvers', siteId],
    queryFn: async () => (await api.get<LogApprover[]>(`/sites/${siteId}/approvers`)).data,
    enabled: open && !!siteId,
  });

  useEffect(() => {
    if (current.data) setRows(current.data.map((a) => ({ userId: a.userId, name: a.name })));
  }, [current.data]);

  const save = useMutation({
    mutationFn: async () =>
      api.put(`/sites/${siteId}/approvers`, { userIds: rows.map((r) => r.userId) }),
    onSuccess: async () => {
      message.success('기본 확인자를 저장했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['site-approvers', siteId] });
      onClose();
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const chosen = new Set(rows.map((r) => r.userId));

  const swap = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setRows(next);
  };

  return (
    <Modal
      open={open}
      title={
        <Space>
          <span>기본 확인자 — {siteName}</span>
          <PageHelp title="일지 기본 확인자 — 화면 설명">
            <HelpP>
              이 사업장의 일지를 <b>새로 만들 때</b> 결재란에 자동으로 들어갈 사람을 미리
              정해 두는 곳입니다. 매일 같은 사람을 다시 고르지 않아도 됩니다.
            </HelpP>
            <HelpH>목록에 WORK&ECO 사용자만 나옵니다</HelpH>
            <HelpP>
              회사 전체 구성원이 아니라 <b>WORK&ECO 를 쓰는 사람</b>만 고를 수 있습니다.
              ECO 에 들어올 수 없는 사람을 확인자로 넣으면 그 사람은 확인을 할 수 없고,
              일지가 확인 대기로 남습니다.
            </HelpP>
            <HelpP>
              찾는 사람이 안 보인다면 아직 ECO 사용자가 아닌 것입니다. WORK&Hub 의{' '}
              <b>역할·권한</b> 에서 ECO 역할(환경책임자·일지작성자)을 주거나,{' '}
              <b>구성원</b> 화면에서 ECO 권한을 지정하면 바로 목록에 나타납니다.
            </HelpP>
            <HelpH>순서가 결재란 순서입니다</HelpH>
            <HelpP>
              위에 있는 사람이 서식의 왼쪽 칸에 들어갑니다. 화살표로 순서를 바꾸세요.
            </HelpP>
            <HelpH>여기서 바꿔도 이미 만든 일지는 그대로입니다</HelpH>
            <HelpP>
              앞으로 만들 일지에만 적용됩니다. 이미 작성한 일지의 확인자는 그 일지 화면에서
              바꾸세요.
            </HelpP>
          </PageHelp>
        </Space>
      }
      okText="저장"
      cancelText="닫기"
      okButtonProps={{ loading: save.isPending, style: readOnly ? { display: 'none' } : undefined }}
      onOk={() => save.mutate()}
      onCancel={onClose}
      width={600}
      destroyOnHidden
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Text type="secondary">
          이 사업장의 일지를 새로 만들면 아래 확인자가 순서대로 결재란에 들어갑니다. 일지마다
          따로 바꿀 수 있습니다.
        </Text>

        {!readOnly && (
          <Select
            showSearch
            placeholder="구성원에서 확인자 추가"
            style={{ width: '100%' }}
            value={null}
            loading={members.isLoading}
            optionFilterProp="label"
            onChange={(userId: string) => {
              const member = members.data?.find((m) => m.userId === userId);
              if (member && !chosen.has(userId)) {
                setRows([...rows, { userId, name: member.name }]);
              }
            }}
            options={(members.data ?? [])
              .filter((m) => !chosen.has(m.userId))
              .map((m) => ({
                value: m.userId,
                label: m.hasSignature ? m.name : `${m.name} (서명 미등록)`,
              }))}
          />
        )}

        {rows.length === 0 ? (
          <Empty
            image={null}
            description={<Text type="secondary">등록된 기본 확인자가 없습니다.</Text>}
          />
        ) : (
          <Table<Chosen>
            className="eco-grid"
            rowKey="userId"
            size="small"
            pagination={false}
            dataSource={rows}
            columns={[
              { title: '순서', width: 60, render: (_, __, i) => i + 1 },
              { title: '이름', dataIndex: 'name' },
              {
                title: '',
                width: 150,
                render: (_, __, i) =>
                  !readOnly && (
                    <Space size={0}>
                      <Tooltip title="위로">
                        <Button
                          type="link"
                          size="small"
                          icon={<UpOutlined />}
                          disabled={i === 0}
                          onClick={() => swap(i, -1)}
                        />
                      </Tooltip>
                      <Tooltip title="아래로">
                        <Button
                          type="link"
                          size="small"
                          icon={<DownOutlined />}
                          disabled={i === rows.length - 1}
                          onClick={() => swap(i, 1)}
                        />
                      </Tooltip>
                      <Button
                        type="link"
                        size="small"
                        danger
                        onClick={() => setRows(rows.filter((_, j) => j !== i))}
                      >
                        삭제
                      </Button>
                    </Space>
                  ),
              },
            ]}
          />
        )}
      </Space>
    </Modal>
  );
}
